/**
 * Zustand store for Token Examiner state management
 */
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Socket } from 'socket.io-client';
import {
  DiscoveredRelationship,
  EntityInfo,
  TokenExaminationStatus,
  RelationshipFilters,
  SortOptions,
  ViewMode,
  InteractionState,
  NetworkInsights,
  RelationshipSnapshot,
} from '../types';

interface TokenExaminerState {
  // Core data
  selectedEntityId: string | null;
  selectedEntity: EntityInfo | null;
  relationships: DiscoveredRelationship[];
  entities: Map<string, EntityInfo>;
  examinationStatus: TokenExaminationStatus | null;
  networkInsights: NetworkInsights | null;

  // Temporal data
  relationshipSnapshots: Map<string, RelationshipSnapshot[]>;
  selectedTimeRange: [Date, Date] | null;

  // UI state
  viewMode: ViewMode;
  filters: RelationshipFilters;
  sortOptions: SortOptions;
  interactionState: InteractionState;

  // Loading states
  isLoading: boolean;
  isExamining: boolean;
  loadingOperations: Set<string>;

  // Error state
  error: string | null;

  // WebSocket
  socket: Socket | null;
  isConnected: boolean;
  activeUsers: Map<string, { name: string; cursor?: { x: number; y: number } }>;

  // Actions - Entity selection
  selectEntity: (entityId: string, entity: EntityInfo) => void;
  clearEntitySelection: () => void;

  // Actions - Relationship management
  setRelationships: (relationships: DiscoveredRelationship[]) => void;
  addRelationship: (relationship: DiscoveredRelationship) => void;
  updateRelationship: (id: string, updates: Partial<DiscoveredRelationship>) => void;
  confirmRelationship: (id: string, confirmedBy: string) => void;
  rejectRelationship: (id: string) => void;

  // Actions - Batch operations
  batchConfirmRelationships: (ids: string[]) => void;
  batchUpdateRelationships: (updates: Array<{ id: string; data: Partial<DiscoveredRelationship> }>) => void;

  // Actions - Filtering and sorting
  setFilters: (filters: Partial<RelationshipFilters>) => void;
  clearFilters: () => void;
  setSortOptions: (options: SortOptions) => void;

  // Actions - View management
  setViewMode: (mode: ViewMode) => void;
  setInteractionState: (state: Partial<InteractionState>) => void;
  highlightRelationship: (id: string) => void;
  selectRelationships: (ids: string[]) => void;
  clearSelection: () => void;

  // Actions - Examination
  setExaminationStatus: (status: TokenExaminationStatus) => void;
  startExamination: () => void;
  completeExamination: () => void;

  // Actions - Network insights
  setNetworkInsights: (insights: NetworkInsights) => void;

  // Actions - Temporal data
  addRelationshipSnapshot: (relationshipId: string, snapshot: RelationshipSnapshot) => void;
  setTimeRange: (range: [Date, Date]) => void;

  // Actions - WebSocket
  connectWebSocket: (entityId: string, userId: string) => void;
  disconnectWebSocket: () => void;

  // Actions - Loading and errors
  setLoading: (loading: boolean) => void;
  addLoadingOperation: (operation: string) => void;
  removeLoadingOperation: (operation: string) => void;
  setError: (error: string | null) => void;

  // Computed getters
  getFilteredRelationships: () => DiscoveredRelationship[];
  getSortedRelationships: () => DiscoveredRelationship[];
  getRelationshipById: (id: string) => DiscoveredRelationship | undefined;
  getEntityRelationships: (entityHash: string) => DiscoveredRelationship[];
  getHealthStatistics: () => {
    average: number;
    distribution: Record<string, number>;
    trending: 'up' | 'down' | 'stable';
  };
}

// Faana color schemes based on scoring
export const getFaanaHealthColor = (score: number): string => {
  if (score >= 80) return '#4CAF50'; // Green - Thriving
  if (score >= 60) return '#FBC02D'; // Yellow - Healthy
  if (score >= 40) return '#FF9800'; // Orange - Concerning
  return '#D32F2F'; // Red - Critical
};

export const getFaanaZoneColor = (zone: string): string => {
  switch (zone) {
    case 'learning': return '#2196F3'; // Blue - Optimal
    case 'comfort': return '#FFC107'; // Amber - Risk of stagnation
    case 'panic': return '#F44336'; // Red - Dysfunction
    default: return '#9E9E9E'; // Grey - Unknown
  }
};

export const getConfidenceOpacity = (confidence: string): number => {
  switch (confidence) {
    case 'HIGH': return 1.0;
    case 'MEDIUM': return 0.7;
    case 'LOW': return 0.4;
    default: return 0.5;
  }
};

const useTokenExaminerStore = create<TokenExaminerState>()(
  devtools(
    // eslint-disable-next-line max-lines-per-function
    (set, get) => ({
      // Initial state
      selectedEntityId: null,
      selectedEntity: null,
      relationships: [],
      entities: new Map(),
      examinationStatus: null,
      networkInsights: null,
      relationshipSnapshots: new Map(),
      selectedTimeRange: null,
      viewMode: 'network',
      filters: {},
      sortOptions: { field: 'health', direction: 'desc' },
      interactionState: {
        selectedRelationshipIds: [],
        highlightedEntityIds: [],
        zoomLevel: 1,
        panPosition: { x: 0, y: 0 },
      },
      isLoading: false,
      isExamining: false,
      loadingOperations: new Set(),
      error: null,
      socket: null,
      isConnected: false,
      activeUsers: new Map(),

      // Entity selection
      selectEntity: (entityId, entity) => set({
        selectedEntityId: entityId,
        selectedEntity: entity,
        relationships: [], // Clear relationships when switching entities
        error: null,
      }),

      clearEntitySelection: () => set({
        selectedEntityId: null,
        selectedEntity: null,
        relationships: [],
        examinationStatus: null,
        networkInsights: null,
      }),

      // Relationship management
      setRelationships: (relationships) => {
        // Extract entities from relationships
        const entities = new Map<string, EntityInfo>();
        relationships.forEach(rel => {
          if (rel.sourceEntity) entities.set(rel.sourceEntity.crossEntityHash, rel.sourceEntity);
          if (rel.targetEntity) entities.set(rel.targetEntity.crossEntityHash, rel.targetEntity);
        });

        set({ relationships, entities });
      },

      addRelationship: (relationship) => set(state => ({
        relationships: [...state.relationships, relationship],
      })),

      updateRelationship: (id, updates) => set(state => ({
        relationships: state.relationships.map(rel =>
          rel.id === id ? { ...rel, ...updates } : rel
        ),
      })),

      confirmRelationship: (id, confirmedBy) => {
        const timestamp = new Date();
        set(state => ({
          relationships: state.relationships.map(rel =>
            rel.id === id
              ? { ...rel, isConfirmed: true, confirmedBy, confirmedAt: timestamp }
              : rel
          ),
        }));

        // Emit WebSocket event
        const { socket } = get();
        if (socket) {
          socket.emit('relationship:confirmed', { relationshipId: id, confirmedBy });
        }
      },

      rejectRelationship: (id) => set(state => ({
        relationships: state.relationships.filter(rel => rel.id !== id),
      })),

      // Batch operations
      batchConfirmRelationships: (ids) => {
        const timestamp = new Date();
        const confirmedBy = 'current-user'; // Should come from auth context

        set(state => ({
          relationships: state.relationships.map(rel =>
            ids.includes(rel.id)
              ? { ...rel, isConfirmed: true, confirmedBy, confirmedAt: timestamp }
              : rel
          ),
        }));
      },

      batchUpdateRelationships: (updates) => {
        const updateMap = new Map(updates.map(u => [u.id, u.data]));

        set(state => ({
          relationships: state.relationships.map(rel => {
            const update = updateMap.get(rel.id);
            return update ? { ...rel, ...update } : rel;
          }),
        }));
      },

      // Filtering and sorting
      setFilters: (filters) => set(state => ({
        filters: { ...state.filters, ...filters },
      })),

      clearFilters: () => set({ filters: {} }),

      setSortOptions: (options) => set({ sortOptions: options }),

      // View management
      setViewMode: (mode) => set({ viewMode: mode }),

      setInteractionState: (state) => set(prev => ({
        interactionState: { ...prev.interactionState, ...state },
      })),

      highlightRelationship: (id) => set(state => ({
        interactionState: {
          ...state.interactionState,
          focusedRelationshipId: id,
        },
      })),

      selectRelationships: (ids) => set(state => ({
        interactionState: {
          ...state.interactionState,
          selectedRelationshipIds: ids,
        },
      })),

      clearSelection: () => set(state => ({
        interactionState: {
          ...state.interactionState,
          selectedRelationshipIds: [],
          focusedRelationshipId: undefined,
        },
      })),

      // Examination
      setExaminationStatus: (status) => set({ examinationStatus: status }),

      startExamination: () => set({ isExamining: true, error: null }),

      completeExamination: () => set({ isExamining: false }),

      // Network insights
      setNetworkInsights: (insights) => set({ networkInsights: insights }),

      // Temporal data
      addRelationshipSnapshot: (relationshipId, snapshot) => set(state => {
        const snapshots = state.relationshipSnapshots.get(relationshipId) || [];
        return {
          relationshipSnapshots: new Map(state.relationshipSnapshots).set(
            relationshipId,
            [...snapshots, snapshot].sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
          ),
        };
      }),

      setTimeRange: (range) => set({ selectedTimeRange: range }),

      // WebSocket connection
      connectWebSocket: () => {
        // WebSocket functionality temporarily disabled - no server implementation
        console.warn('WebSocket functionality is currently disabled for TokenExaminer');
      },

      disconnectWebSocket: () => {
        const { socket } = get();
        if (socket) {
          socket.disconnect();
          set({ socket: null, isConnected: false });
        }
      },

      // Loading and errors
      setLoading: (loading) => set({ isLoading: loading }),

      addLoadingOperation: (operation) => set(state => ({
        loadingOperations: new Set(state.loadingOperations).add(operation),
      })),

      removeLoadingOperation: (operation) => set(state => {
        const ops = new Set(state.loadingOperations);
        ops.delete(operation);
        return { loadingOperations: ops };
      }),

      setError: (error) => set({ error }),

      // Computed getters
      getFilteredRelationships: () => {
        const { relationships, filters } = get();
        let filtered = [...relationships];

        if (filters.relationshipTypes?.length) {
          filtered = filtered.filter(r => filters.relationshipTypes!.includes(r.relationshipType));
        }

        if (filters.confidenceLevels?.length) {
          filtered = filtered.filter(r => filters.confidenceLevels!.includes(r.confidence));
        }

        if (filters.healthRange) {
          const [min, max] = filters.healthRange;
          filtered = filtered.filter(r => r.overallHealth >= min && r.overallHealth <= max);
        }

        if (filters.confirmationStatus && filters.confirmationStatus !== 'all') {
          filtered = filtered.filter(r =>
            filters.confirmationStatus === 'confirmed' ? r.isConfirmed : !r.isConfirmed
          );
        }

        if (filters.behavioralZones?.length) {
          filtered = filtered.filter(r =>
            r.behavioralZone && filters.behavioralZones!.includes(r.behavioralZone)
          );
        }

        return filtered;
      },

      getSortedRelationships: () => {
        const filtered = get().getFilteredRelationships();
        const { sortOptions } = get();

        return filtered.sort((a, b) => {
          let comparison = 0;

          switch (sortOptions.field) {
            case 'health':
              comparison = a.overallHealth - b.overallHealth;
              break;
            case 'confidence':
              {
              const confOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
              comparison = confOrder[a.confidence] - confOrder[b.confidence];
              break;
              }
            case 'discovered':
              comparison = new Date(a.discoveredAt).getTime() - new Date(b.discoveredAt).getTime();
              break;
            case 'name':
              {
              {
              const aName = a.targetEntity?.name || '';
              const bName = b.targetEntity?.name || '';
              comparison = aName.localeCompare(bName);
              break;
              }
              }
          }

          return sortOptions.direction === 'asc' ? comparison : -comparison;
        });
      },

      getRelationshipById: (id) => {
        return get().relationships.find(r => r.id === id);
      },

      getEntityRelationships: (entityHash) => {
        return get().relationships.filter(
          r => r.sourceEntityHash === entityHash || r.targetEntityHash === entityHash
        );
      },

      getHealthStatistics: () => {
        const relationships = get().relationships;
        if (relationships.length === 0) {
          return { average: 0, distribution: {}, trending: 'stable' };
        }

        const total = relationships.reduce((sum, r) => sum + r.overallHealth, 0);
        const average = total / relationships.length;

        const distribution = relationships.reduce((dist, r) => {
          const category = r.healthCategory || 'unknown';
          dist[category] = (dist[category] || 0) + 1;
          return dist;
        }, {} as Record<string, number>);

        // Simple trending based on recent vs older relationships
        const recent = relationships
          .filter(r => new Date(r.discoveredAt) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
          .reduce((sum, r) => sum + r.overallHealth, 0) / relationships.length || 0;

        const trending = recent > average + 5 ? 'up' : recent < average - 5 ? 'down' : 'stable';

        return { average, distribution, trending };
      },
    }),
    {
      name: 'token-examiner-store',
    }
  )
);

export default useTokenExaminerStore;